import { Fragment, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { pagePath } from '../lib/pages'
import { formatAgo, formatDateTime } from '../lib/format'
import { useAuth } from '../lib/auth'
import type { Alert } from '../lib/types'
import { StatusBadge } from '../components/Status'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { AlarmAssistent } from '../components/AlarmAssistent'

interface Props {
  customerSlug: string | null
}

type Filter = 'alle' | 'crit' | 'warn' | 'unterdrueckt'

export function AlarmePage({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const { can, canForCustomer } = useAuth()
  const [filter, setFilter] = useState<Filter>('alle')
  const [offen, setOffen] = useState<string | null>(null)

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['alerts', 'page'],
    queryFn: ({ signal }) => apiGet<Alert[]>('/alerts?unterdrueckt=1', signal),
    refetchInterval: 20_000,
  })

  const acknowledge = useMutation({
    mutationFn: (id: string) => apiSend<void>('POST', `/alerts/${id}/acknowledge`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  })

  if (isError) return <ErrorBox error={error} what="Die Alarme" />
  if (isPending) return <Loading what="Alarme" />

  const suppressed = data.filter((a) => a.suppressed_by_alert_id || a.maintenance_window_id)
  const active = data.filter((a) => !a.suppressed_by_alert_id && !a.maintenance_window_id)
  const counts = {
    alle: active.length,
    crit: active.filter((a) => a.severity === 'crit').length,
    warn: active.filter((a) => a.severity === 'warn').length,
    unterdrueckt: suppressed.length,
  }

  const shown =
    filter === 'unterdrueckt'
      ? suppressed
      : filter === 'alle'
        ? active
        : active.filter((a) => a.severity === filter)

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Alarme</h1>
          <div className="page-sub">
            {customerSlug ?? 'Alle Kunden'} · {counts.alle} offen, {counts.unterdrueckt} unterdrückt
          </div>
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">
            {filter === 'unterdrueckt' ? 'Unterdrückte Alarme' : 'Offene Alarme'}
          </h2>
          <div className="row" style={{ gap: 6 }}>
            {(
              [
                ['alle', `Alle ${counts.alle}`],
                ['crit', `Kritisch ${counts.crit}`],
                ['warn', `Warnung ${counts.warn}`],
                ['unterdrueckt', `Unterdrückt ${counts.unterdrueckt}`],
              ] as [Filter, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className="btn btn-sm"
                aria-pressed={filter === key}
                style={
                  filter === key
                    ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 }
                    : undefined
                }
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 ? (
          <EmptyState
            title={filter === 'unterdrueckt' ? 'Nichts unterdrückt' : 'Keine offenen Alarme'}
            hint={
              filter === 'unterdrueckt'
                ? 'Hier stehen Folgefehler hinter einem ausgefallenen Gerät und Alarme in einem Wartungsfenster.'
                : 'Alles ruhig.'
            }
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Schwere</th>
                <th>Alarm</th>
                <th>Wo</th>
                <th>Seit</th>
                <th>Quittiert</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <Fragment key={a.id}>
                  <tr>
                    <td>
                      {a.suppressed_by_alert_id || a.maintenance_window_id ? (
                        <StatusBadge
                          state="off"
                          label={a.maintenance_window_id ? 'Wartung' : 'Folgefehler'}
                        />
                      ) : (
                        <StatusBadge
                          state={a.severity === 'crit' ? 'crit' : 'warn'}
                          label={a.severity === 'crit' ? 'Kritisch' : 'Warnung'}
                        />
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{a.title}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {a.detail}
                      </div>
                    </td>
                    <td className="muted">
                      {a.device_id ? (
                        <Link
                          to={`${pagePath('geraete', a.customer_slug ?? customerSlug)}/${a.device_id}`}
                        >
                          {a.device_name}
                        </Link>
                      ) : (
                        a.site_name
                      )}
                      <div style={{ fontSize: 12 }}>{a.customer_name}</div>
                    </td>
                    <td className="muted mono" title={formatDateTime(a.opened_at)}>
                      {formatAgo(a.opened_at)}
                    </td>
                    <td className="muted">
                      {a.acknowledged_at ? (
                        <>
                          {a.acknowledged_by_name || 'jemandem'}
                          <div style={{ fontSize: 12 }}>{formatAgo(a.acknowledged_at)}</div>
                        </>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          aria-expanded={offen === a.id}
                          onClick={() => setOffen(offen === a.id ? null : a.id)}
                        >
                          Assistent
                        </button>
                        {!a.acknowledged_at && can('alert.ack') ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={acknowledge.isPending}
                            onClick={() => acknowledge.mutate(a.id)}
                          >
                            Quittieren
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {offen === a.id ? (
                    <tr>
                      <td colSpan={6}>
                        <AlarmAssistent alertId={a.id} darfNotieren={
                          a.customer_id ? canForCustomer(a.customer_id, 'alert.ack') : can('alert.ack')
                        } />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {acknowledge.isError ? (
          <div className="card-body" style={{ color: 'var(--crit)' }}>
            {(acknowledge.error as Error).message}
          </div>
        ) : null}
      </section>

      {filter !== 'unterdrueckt' && suppressed.length > 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {suppressed.length} weitere Alarme sind unterdrückt, weil ein übergeordnetes Gerät oder
          der Agent ausgefallen ist oder ein Wartungsfenster läuft.
        </p>
      ) : null}
    </>
  )
}
